import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Badge, StatCard } from "@/components/Common";
import { sortByUnit } from "@/lib/unitSort";
import { useAuth } from "@/contexts/AuthContext";
import MessagesPanel from "@/components/MessagesPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, ClipboardTextIcon, UserIcon, WrenchIcon, CheckCircleIcon, ClockClockwiseIcon, FireIcon, ChatCircleDotsIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import JobMessages from "@/components/JobMessages";

const PRIORITY_TONES = { low: "neutral", medium: "info", high: "warning", urgent: "danger" };
const STATUS_TONES = { open: "info", assigned: "warning", in_progress: "warning", completed: "success", cancelled: "neutral" };
const STATUS_LABELS = { open: "Open", assigned: "Assigned", in_progress: "In Progress", completed: "Completed", cancelled: "Cancelled" };

const empty = {
  title: "",
  description: "",
  asset_kind: "truck",
  truck_id: "",
  trailer_id: "",
  reported_by: "",
  priority: "medium",
  status: "open",
  assigned_to: "",
  due_date: "",
};

export default function Jobs() {
  const { user } = useAuth();
  const isMechanic = user?.role === "mechanic";
  const isDispatcher = user?.role === "admin" || user?.role === "manager";

  const [jobs, setJobs] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [progressJob, setProgressJob] = useState(null);
  const [progressNotes, setProgressNotes] = useState("");
  const [expandedMessages, setExpandedMessages] = useState(null);

  const load = useCallback(async () => {
    const reqs = [api.get("/jobs"), api.get("/trucks"), api.get("/trailers")];
    if (isDispatcher) reqs.push(api.get("/users"));
    const [j, t, tr, u] = await Promise.all(reqs);
    setJobs(j.data);
    setTrucks(t.data);
    setTrailers(tr.data);
    if (u) setMechanics(u.data.filter((x) => x.role === "mechanic"));
  }, [isDispatcher]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...empty, truck_id: trucks[0]?.id || "" });
    setOpen(true);
  };

  const openEdit = (j) => {
    setEditing(j);
    setForm({
      ...empty,
      ...j,
      asset_kind: j.trailer_id ? "trailer" : "truck",
      truck_id: j.truck_id || "",
      trailer_id: j.trailer_id || "",
      assigned_to: j.assigned_to || "",
      due_date: j.due_date ? j.due_date.slice(0, 10) : "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        truck_id: form.asset_kind === "truck" ? (form.truck_id || null) : null,
        trailer_id: form.asset_kind === "trailer" ? (form.trailer_id || null) : null,
        assigned_to: form.assigned_to || null,
        due_date: form.due_date || null,
      };
      delete body.asset_kind;
      if (editing) await api.put(`/jobs/${editing.id}`, body);
      else await api.post("/jobs", body);
      toast.success(editing ? "Job updated" : "Job posted to mechanic queue");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const del = async (j) => {
    if (!window.confirm(`Delete job "${j.title}"?`)) return;
    await api.delete(`/jobs/${j.id}`);
    toast.success("Deleted");
    await load();
  };

  const claim = async (j) => {
    await api.post(`/jobs/${j.id}/assign`, { assigned_to: user.id });
    toast.success("Job claimed");
    await load();
  };

  const setStatus = async (j, status, extraNotes) => {
    const body = { status };
    if (extraNotes !== undefined) body.mechanic_notes = extraNotes;
    await api.post(`/jobs/${j.id}/status`, body);
    toast.success(`Marked ${STATUS_LABELS[status]}`);
    await load();
  };

  const openProgress = (j) => {
    setProgressJob(j);
    setProgressNotes(j.mechanic_notes || "");
  };

  const saveProgress = async () => {
    await setStatus(progressJob, progressJob.status === "assigned" ? "in_progress" : progressJob.status, progressNotes);
    setProgressJob(null);
  };

  const filtered = statusFilter === "all" ? jobs : jobs.filter((j) => j.status === statusFilter);
  const counts = {
    open: jobs.filter((j) => j.status === "open").length,
    assigned: jobs.filter((j) => j.status === "assigned").length,
    in_progress: jobs.filter((j) => j.status === "in_progress").length,
    completed: jobs.filter((j) => j.status === "completed").length,
  };

  return (
    <div data-testid="jobs-page">
      <PageHeader
        eyebrow={isMechanic ? "Mechanic portal" : "Work orders"}
        title={isMechanic ? "My Jobs" : "Jobs"}
        subtitle={isMechanic ? "Jobs assigned to you and open jobs you can claim." : "Dispatch work orders to mechanics when drivers report issues."}
      >
        {isDispatcher && (
          <Button onClick={openAdd} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-job-button">
            <PlusIcon size={16} weight="bold" className="mr-2" /> New Job
          </Button>
        )}
      </PageHeader>

      <div className="p-8 space-y-6">
        {/* Messages — mechanic ↔ dispatch */}
        {isMechanic && user?.id && <MessagesPanel currentUserId={user.id} compact />}

        {/* Status counters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Open" value={counts.open} icon={ClipboardTextIcon} tone="info" testid="job-stat-open" />
          <StatCard label="Assigned" value={counts.assigned} icon={UserIcon} tone="warning" testid="job-stat-assigned" />
          <StatCard label="In Progress" value={counts.in_progress} icon={WrenchIcon} tone="warning" testid="job-stat-progress" />
          <StatCard label="Completed" value={counts.completed} icon={CheckCircleIcon} tone="success" testid="job-stat-completed" />
        </div>

        {/* Filter */}
        <div className="bg-white border border-slate-300 rounded-sm">
          <div className="p-3 border-b border-slate-200 flex items-center gap-3">
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Filter</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44 rounded-sm" data-testid="job-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <span className="font-mono text-xs text-slate-500 ml-auto">{filtered.length} of {jobs.length}</span>
          </div>

          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <ClipboardTextIcon size={40} weight="thin" className="mx-auto text-slate-400 mb-2" />
              <div className="font-mono text-xs text-slate-400 mb-2">/// NO JOBS</div>
              <div className="text-sm text-slate-600">
                {isMechanic ? "Nothing assigned to you yet — sit tight." : "Post a job to dispatch work to your mechanics."}
              </div>
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {filtered.map((j) => (
                <div key={j.id} className="p-4 hover:bg-slate-50" data-testid={`job-card-${j.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge tone={PRIORITY_TONES[j.priority]}>
                          {j.priority === "urgent" && <FireIcon size={10} weight="fill" />}
                          {j.priority?.toUpperCase()}
                        </Badge>
                        <Badge tone={STATUS_TONES[j.status]}>{STATUS_LABELS[j.status]}</Badge>
                        {j.asset_label && <span className="font-mono text-xs text-slate-600">{j.asset_label}</span>}
                        {(j.messages?.length > 0 || j.attachments?.length > 0) && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-amber-400">
                            <ChatCircleDotsIcon size={11} weight="bold" /> {j.messages?.length || 0} · 📎 {j.attachments?.length || 0}
                          </span>
                        )}
                      </div>
                      <div className="font-display text-lg font-bold tracking-tight text-slate-900">{j.title}</div>
                      {j.description && <p className="text-sm text-slate-600 mt-1">{j.description}</p>}
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 font-mono flex-wrap">
                        {j.reported_by && <span>Reported by: <span className="font-semibold text-slate-700">{j.reported_by}</span></span>}
                        {j.assigned_to_name ? (
                          <span>Mechanic: <span className="font-semibold text-blue-700">{j.assigned_to_name}</span></span>
                        ) : (
                          <span className="text-amber-700 font-semibold">Unassigned</span>
                        )}
                        {j.due_date && <span>Due: {j.due_date.slice(0, 10)}</span>}
                        {j.created_by_name && <span>Posted by: {j.created_by_name}</span>}
                      </div>
                      {j.mechanic_notes && (
                        <div className="mt-2 border-l-2 border-blue-500 bg-blue-50 px-3 py-2 text-xs">
                          <div className="font-mono text-[10px] uppercase tracking-widest text-blue-700 font-bold mb-0.5">Mechanic notes</div>
                          <p className="text-slate-800">{j.mechanic_notes}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Mechanic actions */}
                      {isMechanic && j.status === "open" && (
                        <Button onClick={() => claim(j)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm" data-testid={`claim-job-${j.id}`}>
                          Claim
                        </Button>
                      )}
                      {isMechanic && j.assigned_to === user.id && j.status === "assigned" && (
                        <Button onClick={() => setStatus(j, "in_progress")} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm" data-testid={`start-job-${j.id}`}>
                          Start
                        </Button>
                      )}
                      {isMechanic && j.assigned_to === user.id && j.status === "in_progress" && (
                        <Button onClick={() => setStatus(j, "completed")} className="bg-green-600 hover:bg-green-700 text-white rounded-sm" data-testid={`complete-job-${j.id}`}>
                          <CheckCircleIcon size={14} weight="bold" className="mr-1" /> Complete
                        </Button>
                      )}
                      {(j.assigned_to === user.id || isDispatcher) && j.status !== "completed" && j.status !== "cancelled" && (
                        <Button variant="outline" onClick={() => openProgress(j)} className="rounded-sm" data-testid={`notes-job-${j.id}`}>
                          <ClockClockwiseIcon size={14} weight="bold" className="mr-1" /> Notes
                        </Button>
                      )}
                      {isDispatcher && (
                        <>
                          <Button variant="outline" onClick={() => openEdit(j)} className="rounded-sm" data-testid={`edit-job-${j.id}`}>Edit</Button>
                          <button onClick={() => del(j)} className="text-red-600 hover:underline text-xs font-semibold px-2" data-testid={`del-job-${j.id}`}>Delete</button>
                        </>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => setExpandedMessages(expandedMessages === j.id ? null : j.id)}
                        className="rounded-sm"
                        data-testid={`msg-toggle-${j.id}`}
                      >
                        <ChatCircleDotsIcon size={14} weight="bold" className="mr-1" />
                        {expandedMessages === j.id ? "Hide" : "Messages"}
                      </Button>
                    </div>
                  </div>
                  {expandedMessages === j.id && (
                    <div className="mt-4">
                      <JobMessages jobId={j.id} currentUserId={user?.id} currentRole={user?.role} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dispatch dialog (admin/manager only) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="job-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit job" : "Post new job"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Title *</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 rounded-sm" data-testid="job-title" placeholder="e.g. Cab AC not blowing cold air" />
            </div>

            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 rounded-sm" data-testid="job-description" placeholder="What the driver reported, symptoms, when it started..." />
            </div>

            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700 mb-2 block">Asset *</Label>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <button type="button" onClick={() => setForm({ ...form, asset_kind: "truck", trailer_id: "" })} data-testid="job-asset-truck" className={`px-3 py-2 border-2 rounded-sm text-sm font-semibold transition-colors duration-150 ${form.asset_kind === "truck" ? "border-blue-600 bg-blue-50" : "border-slate-300 hover:bg-slate-50"}`}>Truck</button>
                <button type="button" onClick={() => setForm({ ...form, asset_kind: "trailer", truck_id: "" })} data-testid="job-asset-trailer" className={`px-3 py-2 border-2 rounded-sm text-sm font-semibold transition-colors duration-150 ${form.asset_kind === "trailer" ? "border-blue-600 bg-blue-50" : "border-slate-300 hover:bg-slate-50"}`}>Trailer</button>
              </div>
              {form.asset_kind === "truck" ? (
                <Select value={form.truck_id} onValueChange={(v) => setForm({ ...form, truck_id: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="job-truck"><SelectValue placeholder="Select truck" /></SelectTrigger>
                  <SelectContent>
                    {sortByUnit(trucks).map((t) => <SelectItem key={t.id} value={t.id}>{t.unit_number} · {t.make} {t.model}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Select value={form.trailer_id} onValueChange={(v) => setForm({ ...form, trailer_id: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="job-trailer"><SelectValue placeholder="Select trailer" /></SelectTrigger>
                  <SelectContent>
                    {sortByUnit(trailers).map((t) => <SelectItem key={t.id} value={t.id}>{t.unit_number} · {t.trailer_type.replace(/_/g, " ")} · {t.make}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Reported by (driver)</Label>
              <Input value={form.reported_by} onChange={(e) => setForm({ ...form, reported_by: e.target.value })} className="mt-1.5 rounded-sm" data-testid="job-reported-by" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Due date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5 rounded-sm" data-testid="job-due-date" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="job-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Assign to mechanic</Label>
              <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="job-assigned-to"><SelectValue placeholder="Leave for any mechanic to claim" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Unassigned (any mechanic) —</SelectItem>
                  {mechanics.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="col-span-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="job-save">{editing ? "Update" : "Post job"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mechanic notes / progress dialog */}
      <Dialog open={!!progressJob} onOpenChange={(o) => !o && setProgressJob(null)}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="job-notes-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{progressJob?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Mechanic notes (work performed, parts needed, etc.)</Label>
            <Textarea value={progressNotes} onChange={(e) => setProgressNotes(e.target.value)} className="rounded-sm min-h-[120px]" data-testid="job-progress-notes" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setProgressJob(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={saveProgress} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="job-progress-save">Save notes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
